import SvgIcon from '@mui/material/SvgIcon';
import { useTheme, ClassNameMap } from '@mui/material/styles';
import classNames from 'classnames';

import { useInject } from '@vk-hr-tek/core/ioc';
import { Calendar } from '@vk-hr-tek/core/calendar';

import type { LogoIconVariants } from '../../../types';

import { useStyles } from './LogoEmptyIconText.styles';

interface LogoEmptyIconTextProps {
  variant?: LogoIconVariants;
}

const getSizeClass = (variant: LogoIconVariants, classes: ClassNameMap) => {
  switch (variant) {
    case '40':
      return classes.icon40;
    case '48':
      return classes.icon48;
    case '72':
      return classes.icon72;
    case '80':
      return classes.icon80;
    default:
      return classes.icon48;
  }
};

export const LogoEmptyIconText = ({
  variant = '48',
}: LogoEmptyIconTextProps) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const calendar = useInject(Calendar);

  const sizeClass = getSizeClass(variant, classes);

  if (calendar.isTimeForNewYear()) {
    return (
      <svg
        width="116"
        height="32"
        viewBox="0 0 116 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.4241 23.4941H19.2363L24.8347 8.15991H22.0682L17.5807 20.797H17.1451L12.8101 8.15991H10L15.4241 23.4941Z"
          fill={theme.tokens.colors.text.light.primary}
        />
        <path
          d="M39.1504 23.4941L32.0924 15.5551L38.6058 8.15991H35.3818L29.9358 14.4023H29.3041V8.15991H26.6901V23.4941H29.3041V16.9254H29.9576L35.7085 23.4941H39.1504Z"
          fill={theme.tokens.colors.text.light.primary}
        />
        <path
          d="M55.4479 14.7945V8.36607H58.0758V23.7813H55.4479V17.1997H48.1118V23.7813H45.484V8.36607H48.1118V14.7945H55.4479Z"
          fill={theme.tokens.colors.text.light.primary}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M73.0022 23.7813H69.9145L65.8413 18.1181H63.9799V23.7813H61.352V8.36607H67.0457C68.0969 8.36607 69.0385 8.57744 69.8707 9.00017C70.7028 9.40833 71.3525 9.98412 71.8197 10.7275C72.2868 11.4564 72.5204 12.2946 72.5204 13.2421C72.5204 14.3937 72.1701 15.3922 71.4693 16.2377C70.7831 17.0685 69.8634 17.6298 68.71 17.9213L73.0022 23.7813ZM63.9799 10.7713V15.7129H67.0457C67.8633 15.7129 68.5275 15.4869 69.0385 15.0351C69.5641 14.5686 69.8269 13.9709 69.8269 13.2421C69.8269 12.5132 69.5641 11.9229 69.0385 11.471C68.5275 11.0045 67.8633 10.7713 67.0457 10.7713H63.9799Z"
          fill={theme.tokens.colors.text.light.primary}
        />
        <path
          d="M83.8325 23.7813H86.4604V10.7713H91.7161V8.36607H78.5768V10.7713H83.8325V23.7813Z"
          fill={theme.tokens.colors.text.light.primary}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M102.348 17.965C102.348 18.1837 102.334 18.4534 102.304 18.7741H93.1506C93.2674 19.6633 93.6324 20.3775 94.2456 20.9169C94.8587 21.4416 95.6398 21.704 96.5887 21.704C97.2311 21.704 97.8078 21.5801 98.3188 21.3323C98.8443 21.0845 99.2458 20.7492 99.5232 20.3265L101.888 20.7201C101.479 21.7696 100.771 22.5787 99.7641 23.1472C98.7713 23.7157 97.6618 23.9999 96.4355 23.9999C95.2967 23.9999 94.2748 23.7521 93.3696 23.2565C92.4791 22.7463 91.7783 22.0466 91.2673 21.1574C90.771 20.2536 90.5228 19.2332 90.5228 18.0962C90.5228 16.9592 90.771 15.9461 91.2673 15.0569C91.7783 14.1531 92.4791 13.4534 93.3696 12.9578C94.2748 12.4476 95.2967 12.1925 96.4355 12.1925C97.5742 12.1925 98.5888 12.4476 99.4794 12.9578C100.385 13.4534 101.085 14.1459 101.582 15.0351C102.093 15.9097 102.348 16.8863 102.348 17.965ZM96.4355 14.3791C95.6763 14.3791 95.012 14.5977 94.4427 15.0351C93.8879 15.4724 93.5083 16.0627 93.3039 16.8062H99.6108C99.3918 16.0773 98.9976 15.4942 98.4283 15.0569C97.8735 14.605 97.2092 14.3791 96.4355 14.3791Z"
          fill={theme.tokens.colors.text.light.primary}
        />
        <path
          d="M111.493 23.7813H114.8L109.566 17.8776L114.362 12.4112H111.362L107.551 16.828H106.938V8.36607H104.42V23.7813H106.938V19.2551H107.551L111.493 23.7813Z"
          fill={theme.tokens.colors.text.light.primary}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M103.471 0.439618C102.628 0.570498 101.776 1.01064 100.978 1.89111L100.974 1.89492L100.974 1.89485C96.4536 6.55298 92.0451 8.04109 88.3868 7.9636C84.8246 7.88815 81.9886 6.32824 80.473 4.79602C79.0696 6.31056 76.649 7.97662 74.0845 8.66066C71.4457 9.36451 68.6289 9.03176 66.6344 6.39337C64.9063 4.10754 62.4702 3.10834 59.9923 2.89531C57.5527 2.68558 55.0805 3.23936 53.2295 4.07259C53.2905 5.23844 53.0306 6.42309 52.562 7.19597C52.3209 7.59346 52.0139 7.89929 51.6493 8.02102C51.2751 8.14598 50.8717 8.06681 50.4706 7.75793C50.1318 7.49707 49.9539 7.17994 49.9396 6.82663C49.9255 6.4797 50.0703 6.1237 50.3217 5.7802C50.8105 5.1124 51.7385 4.4412 52.9159 3.8867C52.8056 2.60727 52.2828 1.41129 51.2056 0.86692C50.1003 0.308381 48.3463 0.40248 45.7107 1.91884C38.5367 6.45239 33.3716 8.01727 29.7776 8.05135C26.266 8.08465 24.2497 6.65484 23.3519 5.10709C20.1658 6.39879 14.4448 6.51552 10.1611 1.89234C10.1048 1.83157 10.1084 1.73666 10.1691 1.68036C10.2299 1.62405 10.3248 1.62767 10.3811 1.68844C14.5491 6.18671 20.1073 6.08512 23.2091 4.84122C22.7381 3.89026 22.6754 2.91791 22.9559 2.25471C23.1046 1.90307 23.354 1.6313 23.6965 1.51185C24.0385 1.39255 24.4466 1.43444 24.895 1.65589C25.3173 1.86441 25.5979 2.12535 25.7261 2.43063C25.8562 2.74029 25.8183 3.06855 25.6586 3.38323C25.3604 3.97072 24.6246 4.54285 23.6301 4.98848C24.4752 6.41696 26.3784 7.78357 29.7747 7.75137C33.2901 7.71803 38.4002 6.18425 45.553 1.66359L45.5583 1.66027L45.5583 1.66038C48.2259 0.125049 50.0988 -0.0285314 51.3409 0.599166C52.5181 1.19407 53.0703 2.46081 53.2048 3.75523C55.0849 2.92829 57.5642 2.38546 60.018 2.59641C62.5602 2.81496 65.0832 3.84398 66.8737 6.21245C68.772 8.72357 71.4452 9.05415 74.0072 8.3708C76.5187 7.70089 78.8987 6.05953 80.266 4.57799C79.6403 3.89042 79.268 3.20071 79.2523 2.65273C79.2437 2.35675 79.3399 2.09408 79.5636 1.90939C79.782 1.72908 80.1013 1.64039 80.5061 1.64039C80.9207 1.64039 81.2422 1.72425 81.4561 1.90156C81.6778 2.08531 81.7585 2.34745 81.7344 2.63939C81.7107 2.92589 81.5867 3.24992 81.3909 3.59017C81.2123 3.90055 80.9693 4.23232 80.6738 4.5724C82.1331 6.05498 84.9016 7.58972 88.3932 7.66367C91.9555 7.73912 96.2862 6.29394 100.757 1.68779C101.595 0.76423 102.506 0.285786 103.425 0.143169C104.342 0.000739016 105.253 0.195326 106.089 0.593628C107.724 1.37287 109.096 2.94358 109.716 4.38737C111.129 4.53778 112.932 4.91493 115.2 5.61578C115.279 5.64024 115.323 5.72424 115.299 5.80338C115.274 5.88253 115.19 5.92686 115.111 5.9024C112.94 5.23128 111.205 4.86147 109.84 4.70303C110.156 5.58821 110.182 6.44572 109.715 7.00148C109.202 7.61126 108.17 7.77352 106.563 7.32228C106.066 7.18282 105.662 6.89998 105.426 6.55231C105.188 6.20264 105.118 5.78026 105.314 5.39066C105.507 5.00654 105.941 4.68939 106.636 4.50066C107.288 4.32383 108.184 4.25459 109.373 4.3546C108.751 3.01467 107.467 1.58284 105.96 0.864443C105.165 0.485818 104.316 0.308453 103.471 0.439618ZM109.507 4.6678C108.258 4.54925 107.349 4.61805 106.715 4.79018C106.064 4.96694 105.722 5.24681 105.582 5.52547C105.444 5.79866 105.484 6.10482 105.674 6.38368C105.865 6.66453 106.205 6.9102 106.644 7.03345C108.233 7.47966 109.096 7.27108 109.485 6.80847C109.867 6.35427 109.861 5.57544 109.507 4.6678ZM80.467 4.35298C80.7439 4.03197 80.9679 3.72376 81.1309 3.44053C81.3158 3.11915 81.4169 2.8393 81.4354 2.6147C81.4535 2.39554 81.3936 2.23935 81.2647 2.13254C81.1281 2.0193 80.889 1.94039 80.5061 1.94039C80.139 1.94039 79.8989 2.0216 79.7546 2.14074C79.6156 2.2555 79.5457 2.42185 79.5521 2.64409C79.5647 3.07901 79.8715 3.69234 80.467 4.35298ZM23.486 4.72429C24.468 4.28782 25.1371 3.74799 25.3911 3.24746C25.5205 2.99252 25.5381 2.75758 25.4496 2.54681C25.3592 2.33167 25.1471 2.11494 24.7622 1.92489C24.3638 1.72817 24.0408 1.70947 23.7953 1.79511C23.5502 1.8806 23.3558 2.07929 23.2322 2.37157C22.9933 2.93639 23.0348 3.82337 23.486 4.72429ZM52.935 4.21C51.8325 4.74335 50.9963 5.36653 50.5638 5.95738C50.3351 6.26984 50.229 6.55983 50.2394 6.81447C50.2494 7.06273 50.3712 7.30277 50.6536 7.52024C50.9927 7.78133 51.2936 7.82354 51.5543 7.73647C51.8248 7.64617 52.0834 7.40657 52.3054 7.04042C52.7206 6.35575 52.97 5.28886 52.935 4.21Z"
          fill="#C3BABC"
        />
        <path
          d="M16.3892 5.56168L17.9244 5.80752L18.0354 6.82968H16.3499L16.3892 5.56168Z"
          fill="#696060"
        />
        <path
          d="M18.7214 7.97644C18.7214 9.37737 17.555 11.4394 17.0405 11.4394C16.5259 11.4394 15.5293 9.37737 15.5293 7.97644C15.5293 6.5755 16.7038 6.40234 17.2183 6.40234C17.7328 6.40234 18.7214 6.5755 18.7214 7.97644Z"
          fill="url(#paint0_linear_17448_1765)"
        />
        <path
          d="M16.0383 8.402C16.2203 9.01779 16.4573 9.91233 16.95 11.1912C16.2314 9.95538 16.0252 9.23939 15.8432 8.62359C15.6613 8.00779 15.8432 7.35242 16.2315 6.89886C16.1366 7.29851 15.8896 7.89851 16.0383 8.402Z"
          fill="#FFECD0"
        />
        <path
          d="M68.3157 7.99813L69.394 8.55128L69.7649 9.25512H68.0794L68.3157 7.99813Z"
          fill="#696060"
        />
        <path
          d="M70.451 10.4019C70.451 11.8028 69.2845 13.8648 68.77 13.8648C68.2554 13.8648 67.2588 11.8028 67.2588 10.4019C67.2588 9.00094 68.4333 8.82778 68.9478 8.82778C69.4623 8.82778 70.451 9.00094 70.451 10.4019Z"
          fill="url(#paint1_linear_17448_1765)"
        />
        <path
          d="M67.7678 10.8274C67.9498 11.4432 68.1868 12.3378 68.6795 13.6166C67.9609 12.3808 67.7547 11.6648 67.5728 11.049C67.3908 10.4332 67.5728 9.77785 67.961 9.3243C67.8661 9.72395 67.6191 10.3239 67.7678 10.8274Z"
          fill="#FFECD0"
        />
        <path
          d="M25.2102 6.9532L26.5344 7.5782L26.7171 8.24983H25.0315L25.2102 6.9532Z"
          fill="#696060"
        />
        <path
          d="M27.4031 9.39658C27.4031 10.7975 26.2366 12.8595 25.7221 12.8595C25.2076 12.8595 24.2109 10.7975 24.2109 9.39658C24.2109 7.99565 25.3854 7.82248 25.8999 7.82248C26.4145 7.82248 27.4031 7.99565 27.4031 9.39658Z"
          fill="url(#paint2_linear_17448_1765)"
        />
        <path
          d="M24.72 9.82214C24.9019 10.4379 25.1389 11.3325 25.6316 12.6113C24.913 11.3755 24.7068 10.6595 24.5249 10.0437C24.3429 9.42793 24.5249 8.77256 24.9131 8.31901C24.8182 8.71865 24.5712 9.31865 24.72 9.82214Z"
          fill="#DCFFD0"
        />
        <path
          d="M78.1762 6.66215L79.2432 5.93175L79.6936 7.33378H78.0081L78.1762 6.66215Z"
          fill="#696060"
        />
        <path
          d="M80.3796 8.48054C80.3796 9.88148 79.2132 11.9435 78.6987 11.9435C78.1841 11.9435 77.1875 9.88148 77.1875 8.48054C77.1875 7.0796 78.362 6.90644 78.8765 6.90644C79.391 6.90644 80.3796 7.0796 80.3796 8.48054Z"
          fill="url(#paint3_linear_17448_1765)"
        />
        <path
          d="M77.6965 8.9061C77.8785 9.52189 78.1155 10.4164 78.6082 11.6953C77.8896 10.4595 77.6834 9.74349 77.5014 9.12769C77.3195 8.51189 77.5014 7.85652 77.8897 7.40296C77.7948 7.80261 77.5478 8.40261 77.6965 8.9061Z"
          fill="#DCFFD0"
        />
        <path
          d="M34.9602 7.1689L36.3499 6.67585L36.6458 7.92606H34.9602V7.1689Z"
          fill="#696060"
        />
        <path
          d="M37.3322 9.07308C37.3322 10.474 36.1658 12.536 35.6512 12.536C35.1367 12.536 34.1401 10.474 34.1401 9.07308C34.1401 7.67214 35.3146 7.49898 35.8291 7.49898C36.3436 7.49898 37.3322 7.67214 37.3322 9.07308Z"
          fill="url(#paint4_linear_17448_1765)"
        />
        <path
          d="M34.6487 9.49835C34.8306 10.1142 35.0676 11.0087 35.5603 12.2875C34.8417 11.0517 34.6355 10.3357 34.4536 9.71995C34.2716 9.10415 34.4536 8.44878 34.8418 7.99522C34.7469 8.39487 34.4999 8.99487 34.6487 9.49835Z"
          fill="#D0E7FF"
        />
        <path
          d="M91.1059 7.74356L92.5527 7.38199L92.7914 8.58395H91.1059V7.74356Z"
          fill="#696060"
        />
        <path
          d="M93.4779 9.73097C93.4779 11.1319 92.3115 13.1939 91.7969 13.1939C91.2824 13.1939 90.2858 11.1319 90.2858 9.73097C90.2858 8.33003 91.4602 8.15687 91.9748 8.15687C92.4893 8.15687 93.4779 8.33003 93.4779 9.73097Z"
          fill="url(#paint5_linear_17448_1765)"
        />
        <path
          d="M90.7943 10.1562C90.9763 10.772 91.2133 11.6666 91.706 12.9454C90.9874 11.7096 90.7812 10.9936 90.5993 10.3778C90.4173 9.76204 90.5993 9.10666 90.9875 8.65311C90.8926 9.05276 90.6456 9.65276 90.7943 10.1562Z"
          fill="#D0E7FF"
        />
        <path
          d="M44.0286 2.92455L45.3475 2.11737L45.7141 3.70843H44.0286V2.92455Z"
          fill="#696060"
        />
        <path
          d="M46.4002 4.85518C46.4002 6.25612 45.2337 8.31812 44.7192 8.31812C44.2046 8.31812 43.208 6.25612 43.208 4.85518C43.208 3.45425 44.3825 3.28108 44.897 3.28108C45.4115 3.28108 46.4002 3.45425 46.4002 4.85518Z"
          fill="url(#paint6_linear_17448_1765)"
        />
        <path
          d="M43.717 5.28074C43.899 5.89654 44.136 6.79107 44.6287 8.06992C43.9101 6.83413 43.7039 6.11813 43.5219 5.50233C43.34 4.88653 43.5219 4.23116 43.9102 3.77761C43.8153 4.17725 43.5683 4.77725 43.717 5.28074Z"
          fill="#FFD0D1"
        />
        <path
          d="M102.026 0.975528L102.525 0.695871L103.135 0.506104L103.611 2.01533H101.925L102.026 0.975528Z"
          fill="#696060"
        />
        <path
          d="M104.297 3.16209C104.297 4.56302 103.13 6.62502 102.616 6.62502C102.101 6.62502 101.105 4.56302 101.105 3.16209C101.105 1.76115 102.279 1.58799 102.794 1.58799C103.308 1.58799 104.297 1.76115 104.297 3.16209Z"
          fill="url(#paint7_linear_17448_1765)"
        />
        <path
          d="M101.614 3.58765C101.796 4.20344 102.033 5.09798 102.525 6.37683C101.807 5.14103 101.6 4.42504 101.419 3.80924C101.237 3.19344 101.419 2.53807 101.807 2.08451C101.712 2.48416 101.465 3.08416 101.614 3.58765Z"
          fill="#FFD0D1"
        />
        <path
          d="M56.4705 3.03293L57.7763 2.89743L58.1561 4.38062H56.4705V3.03293Z"
          fill="#696060"
        />
        <path
          d="M58.9095 5.28263C58.9095 6.68357 57.743 8.74556 57.2285 8.74556C56.714 8.74556 55.7174 6.68357 55.7174 5.28263C55.7174 3.88169 56.8918 3.70853 57.4064 3.70853C57.9209 3.70853 58.9095 3.88169 58.9095 5.28263Z"
          fill="url(#paint8_linear_17448_1765)"
        />
        <path
          d="M56.159 5.95293C56.3409 6.56873 56.5779 7.46326 57.0706 8.74212C56.352 7.50632 56.1458 6.79032 55.9639 6.17452C55.7819 5.55872 55.9639 4.90335 56.3521 4.4498C56.2572 4.84944 56.0102 5.44944 56.159 5.95293Z"
          fill="#FFD0FC"
        />
        <circle
          cx="17.1929"
          cy="9.48193"
          r="4.2749"
          fill="url(#paint9_diamond_17448_1765)"
        />
        <circle
          cx="25.8066"
          cy="10.8955"
          r="4.27539"
          fill="url(#paint10_diamond_17448_1765)"
        />
        <circle
          cx="35.7349"
          cy="11.1392"
          r="4.03174"
          fill="url(#paint11_diamond_17448_1765)"
        />
        <circle
          cx="44.871"
          cy="7.14835"
          r="3.68936"
          fill="url(#paint12_diamond_17448_1765)"
        />
        <circle
          cx="57.3135"
          cy="6.93066"
          r="4.47168"
          fill="url(#paint13_diamond_17448_1765)"
        />
        <circle
          cx="68.8555"
          cy="12.0898"
          r="4.3125"
          fill="url(#paint14_diamond_17448_1765)"
        />
        <circle
          cx="78.7832"
          cy="10.4287"
          r="4.74219"
          fill="url(#paint15_diamond_17448_1765)"
        />
        <circle
          cx="91.8818"
          cy="11.1865"
          r="4.78418"
          fill="url(#paint16_diamond_17448_1765)"
        />
        <circle
          cx="102.769"
          cy="5.02686"
          r="4.38623"
          fill="url(#paint17_diamond_17448_1765)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFB342" />
            <stop offset="1" stopColor="#F69A10" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFB342" />
            <stop offset="1" stopColor="#F69A10" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#9BF0FD" />
            <stop offset="1" stopColor="#0FEE03" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#9BF0FD" />
            <stop offset="1" stopColor="#0FEE03" />
          </linearGradient>
          <linearGradient
            id="paint4_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#5D7DFF" />
            <stop offset="1" stopColor="#0381F6" />
          </linearGradient>
          <linearGradient
            id="paint5_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#5D7DFF" />
            <stop offset="1" stopColor="#0381F6" />
          </linearGradient>
          <linearGradient
            id="paint6_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FD9B9E" />
            <stop offset="1" stopColor="#FF0509" />
          </linearGradient>
          <linearGradient
            id="paint7_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FD9B9E" />
            <stop offset="1" stopColor="#FF0509" />
          </linearGradient>
          <linearGradient
            id="paint8_linear_17448_1765"
            x1="26.7695"
            y1="7.18546"
            x2="38.3925"
            y2="-22.8328"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FF74FA" />
            <stop offset="1" stopColor="#EA05FA" />
          </linearGradient>
          <radialGradient
            id="paint9_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(17.1929 9.48193) rotate(90) scale(4.2749)"
          >
            <stop stopColor="#FFD297" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint10_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(25.8066 10.8955) rotate(90) scale(4.27539)"
          >
            <stop stopColor="#6FEDD4" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint11_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(35.7349 11.1392) rotate(90) scale(4.03174)"
          >
            <stop stopColor="#95B5FF" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint12_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(44.871 7.14835) rotate(90) scale(3.68936)"
          >
            <stop stopColor="#FE90A6" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint13_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(57.3135 6.93066) rotate(90) scale(4.47168)"
          >
            <stop stopColor="#F597FF" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint14_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(68.8555 12.0898) rotate(90) scale(4.3125)"
          >
            <stop stopColor="#FFBA88" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint15_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(78.7832 10.4287) rotate(90) scale(4.74219)"
          >
            <stop stopColor="#9BF9BB" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint16_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(91.8818 11.1865) rotate(90) scale(4.78418)"
          >
            <stop stopColor="#93E6FF" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id="paint17_diamond_17448_1765"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(102.769 5.02686) rotate(90) scale(4.38623)"
          >
            <stop stopColor="#FF95B0" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
    );
  }

  return (
    <SvgIcon
      className={classNames(classes.default, sizeClass)}
      htmlColor="#ffffff"
      viewBox="0 0 174 48"
    >
      <path
        d="M24.1362 35.241H29.8544L38.2521 12.2397H34.1023L27.3711 31.1954H26.7176L20.2151 12.2397H16L24.1362 35.241Z"
        fill={theme.tokens.colors.text.light.primary}
      />
      <path
        d="M59.7255 35.241L49.1386 23.3326L58.9086 12.2397H54.0727L45.9038 21.6034H44.9562V12.2397H41.0351V35.241H44.9562V25.388H45.9364L54.5628 35.241H59.7255Z"
        fill={theme.tokens.colors.text.light.primary}
      />
      <path
        d="M84.1719 22.1917V12.549H88.1137V35.6718H84.1719V25.7995H73.1677V35.6718H69.2259V12.549H73.1677V22.1917H84.1719Z"
        fill={theme.tokens.colors.text.light.primary}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M110.503 35.6718H105.872L99.7619 27.177H96.9698V35.6718H93.0281V12.549H101.569C103.145 12.549 104.558 12.866 105.806 13.5001C107.054 14.1124 108.029 14.9761 108.729 16.0912C109.43 17.1845 109.781 18.4417 109.781 19.863C109.781 21.5904 109.255 23.0882 108.204 24.3564C107.175 25.6027 105.795 26.4445 104.065 26.8818L110.503 35.6718ZM96.9698 16.1568V23.5692H101.569C102.795 23.5692 103.791 23.2303 104.558 22.5525C105.346 21.8528 105.74 20.9563 105.74 19.863C105.74 18.7697 105.346 17.8842 104.558 17.2063C103.791 16.5066 102.795 16.1568 101.569 16.1568H96.9698Z"
        fill={theme.tokens.colors.text.light.primary}
      />
      <path
        d="M126.749 35.6718H130.691V16.1568H138.574V12.549H118.865V16.1568H126.749V35.6718Z"
        fill={theme.tokens.colors.text.light.primary}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M154.522 26.9474C154.522 27.2754 154.5 27.6799 154.456 28.161H140.726C140.901 29.4948 141.449 30.5662 142.368 31.3752C143.288 32.1623 144.46 32.5559 145.883 32.5559C146.847 32.5559 147.712 32.3701 148.478 31.9984C149.266 31.6266 149.869 31.1237 150.285 30.4896L153.832 31.08C153.219 32.6543 152.157 33.8679 150.646 34.7206C149.157 35.5734 147.493 35.9997 145.653 35.9997C143.945 35.9997 142.412 35.628 141.054 34.8846C139.719 34.1193 138.667 33.0698 137.901 31.736C137.156 30.3803 136.784 28.8497 136.784 27.1442C136.784 25.4387 137.156 23.9191 137.901 22.5853C138.667 21.2296 139.719 20.18 141.054 19.4366C142.412 18.6713 143.945 18.2887 145.653 18.2887C147.361 18.2887 148.883 18.6713 150.219 19.4366C151.577 20.18 152.628 21.2187 153.373 22.5525C154.139 23.8644 154.522 25.3294 154.522 26.9474ZM145.653 21.5685C144.514 21.5685 143.518 21.8965 142.664 22.5525C141.832 23.2084 141.262 24.094 140.956 25.2091H150.416C150.088 24.1158 149.496 23.2412 148.642 22.5853C147.81 21.9074 146.814 21.5685 145.653 21.5685Z"
        fill={theme.tokens.colors.text.light.primary}
      />
      <path
        d="M168.24 35.6718H173.2L165.349 26.8162L172.543 18.6167H168.043L162.327 25.2419H161.407V12.549H157.63V35.6718H161.407V28.8825H162.327L168.24 35.6718Z"
        fill={theme.tokens.colors.text.light.primary}
      />
    </SvgIcon>
  );
};
